/**
 * Privacy Policy Page
 * 
 * Required for Apple App Store and Google Play Store submissions
 * Styled to match the main landing page
 */

'use client';

import Link from 'next/link';
import { Mail, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
    const lastUpdated = 'February 6, 2026';
    const companyName = 'TekSpert';
    const appName = 'TekSpert Mail';
    const contactEmail = 'privacy@tekspert.com';
    const websiteUrl = 'https://mail.tekspert.com';

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/40 dark:from-gray-900 dark:via-blue-950/30 dark:to-indigo-950/40">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        <Link href="/" className="flex items-center space-x-3 group">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center transform group-hover:scale-105 transition-transform">
                                <Mail className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-bold text-gray-900 dark:text-white">TekSpert Email</span>
                        </Link>

                        <div className="hidden md:flex items-center space-x-8">
                            <Link href="/#features" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                Features
                            </Link>
                            <Link href="/#security" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                Security
                            </Link>
                            <Link href="/#about" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                About
                            </Link>
                            <Link href="/#contact" className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                Contact
                            </Link>
                        </div>

                        <div className="flex items-center space-x-4">
                            <Link
                                href="/auth/login"
                                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
                            >
                                Sign In
                            </Link>
                            <Link
                                href="/auth/register"
                                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-blue-500/30 transform hover:-translate-y-0.5 transition-all"
                            >
                                Get Started
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-32 pb-20 px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    {/* Back Link */}
                    <Link href="/" className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-8 group">
                        <ArrowLeft className="w-4 h-4 mr-2 transform group-hover:-translate-x-1 transition-transform" />
                        Back to Home
                    </Link>

                    {/* Content Card */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 sm:p-12">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                            Privacy Policy
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400 mb-10">
                            Last updated: {lastUpdated}
                        </p>

                        <div className="prose prose-lg prose-gray dark:prose-invert max-w-none">
                            {/* Introduction */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    1. Introduction
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    Welcome to {appName}. This Privacy Policy explains how {companyName} (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) 
                                    collects, uses, discloses, and safeguards your information when you use our email application 
                                    available on web, iOS, and Android platforms (collectively, the &quot;Service&quot;).
                                </p>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-4">
                                    Please read this Privacy Policy carefully. By using the Service, you agree to the collection 
                                    and use of information in accordance with this policy.
                                </p>
                            </section>

                            {/* Information We Collect */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    2. Information We Collect
                                </h2>
                                
                                <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 mt-6 mb-3">
                                    2.1 Information You Provide
                                </h3>
                                <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-2">
                                    <li><strong>Account Information:</strong> Email address, password, and display name when you create an account.</li>
                                    <li><strong>Email Content:</strong> The emails you send, receive, and store using our Service, including attachments.</li>
                                    <li><strong>Contact Information:</strong> Email addresses of people you correspond with.</li>
                                    <li><strong>Profile Data:</strong> Any additional information you choose to add to your profile.</li>
                                </ul>

                                <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 mt-6 mb-3">
                                    2.2 Information Collected Automatically
                                </h3>
                                <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-2">
                                    <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, and mobile network information.</li>
                                    <li><strong>Usage Data:</strong> How you interact with our Service, including features used and time spent.</li>
                                    <li><strong>Log Data:</strong> IP address, browser type, pages visited, and timestamps.</li>
                                </ul>
                            </section>

                            {/* How We Use Information */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    3. How We Use Your Information
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                                    We use the information we collect to:
                                </p>
                                <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-2">
                                    <li>Provide, maintain, and improve our email services</li>
                                    <li>Send, receive, and store your emails and attachments</li>
                                    <li>Authenticate your identity and secure your account</li>
                                    <li>Send you service-related notifications and updates</li>
                                    <li>Respond to your comments, questions, and support requests</li>
                                    <li>Detect, prevent, and address technical issues and security threats</li>
                                    <li>Comply with legal obligations</li>
                                </ul>
                            </section>

                            {/* Data Storage and Security */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    4. Data Storage and Security
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    We implement industry-standard security measures to protect your data, including:
                                </p>
                                <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                                    <li>Encryption of data in transit using TLS/SSL</li>
                                    <li>Encryption of sensitive data at rest</li>
                                    <li>Regular security audits and vulnerability assessments</li>
                                    <li>Access controls and authentication mechanisms</li>
                                    <li>Secure data centers with physical security measures</li>
                                </ul>
                            </section>

                            {/* Data Sharing */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    5. Information Sharing and Disclosure
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                                    We do not sell your personal information. We may share your information only in the following circumstances:
                                </p>
                                <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-2">
                                    <li><strong>Service Providers:</strong> With trusted third parties who assist in operating our Service.</li>
                                    <li><strong>Legal Requirements:</strong> When required by law, subpoena, or legal process.</li>
                                    <li><strong>Safety:</strong> To protect the rights, property, or safety of our users and others.</li>
                                    <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
                                    <li><strong>With Your Consent:</strong> When you have given us explicit permission.</li>
                                </ul>
                            </section>

                            {/* Your Rights */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    6. Your Rights and Choices
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                                    You have the following rights regarding your personal data:
                                </p>
                                <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 space-y-2">
                                    <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                                    <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                                    <li><strong>Deletion:</strong> Request deletion of your personal data, subject to legal requirements.</li>
                                    <li><strong>Export:</strong> Request a portable copy of your data in a common format.</li>
                                    <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time.</li>
                                </ul>
                            </section>

                            {/* Data Retention */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    7. Data Retention
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    We retain your personal data for as long as your account is active or as needed to provide 
                                    you with our services. We may retain certain information as required by law or for legitimate 
                                    business purposes, such as resolving disputes and enforcing our agreements.
                                </p>
                            </section>

                            {/* Children's Privacy */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    8. Children&apos;s Privacy
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    Our Service is not intended for users under the age of 13. We do not knowingly collect 
                                    personal information from children under 13. If we become aware that we have collected 
                                    data from a child under 13, we will take steps to delete such information.
                                </p>
                            </section>

                            {/* International Transfers */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    9. International Data Transfers
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    Your information may be transferred to and processed in countries other than your country 
                                    of residence. We ensure appropriate safeguards are in place to protect your data in 
                                    accordance with applicable data protection laws.
                                </p>
                            </section>

                            {/* Third-Party Links */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    10. Third-Party Links
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    The Service may contain links to third-party websites or services. We are not responsible 
                                    for the privacy practices of these third parties. We encourage you to review the privacy 
                                    policies of any third-party services you access.
                                </p>
                            </section>

                            {/* Changes to Policy */}
                            <section className="mb-10">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    11. Changes to This Privacy Policy
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    We may update this Privacy Policy from time to time. We will notify you of any changes by 
                                    posting the new Privacy Policy on this page and updating the &quot;Last updated&quot; date. We encourage 
                                    you to review this Privacy Policy periodically for any changes.
                                </p>
                            </section>

                            {/* Contact Us */}
                            <section className="mb-6">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                    12. Contact Us
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                                    If you have any questions about this Privacy Policy or our privacy practices, please contact us:
                                </p>
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-700 rounded-xl p-6">
                                    <p className="text-gray-700 dark:text-gray-300">
                                        <strong className="text-gray-900 dark:text-white">{companyName}</strong><br />
                                        Email: <a href={`mailto:${contactEmail}`} className="text-blue-600 dark:text-blue-400 hover:underline">{contactEmail}</a><br />
                                        Website: <a href={websiteUrl} className="text-blue-600 dark:text-blue-400 hover:underline">{websiteUrl}</a>
                                    </p>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-gray-900 dark:bg-black text-gray-300 py-16">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-12 mb-12">
                        {/* Company Info */}
                        <div>
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                                    <Mail className="w-5 h-5 text-white" />
                                </div>
                                <span className="text-xl font-bold text-white">TekSpert</span>
                            </div>
                            <p className="text-gray-400 leading-relaxed">
                                Enterprise email solutions for modern businesses.
                            </p>
                        </div>

                        {/* Product */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Product</h4>
                            <ul className="space-y-2">
                                <li><Link href="/#features" className="hover:text-blue-400 transition-colors">Features</Link></li>
                                <li><Link href="/#security" className="hover:text-blue-400 transition-colors">Security</Link></li>
                                <li><Link href="/auth/register" className="hover:text-blue-400 transition-colors">Pricing</Link></li>
                                <li><Link href="/#about" className="hover:text-blue-400 transition-colors">About</Link></li>
                            </ul>
                        </div>

                        {/* Support */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Support</h4>
                            <ul className="space-y-2">
                                <li><a href="mailto:support@tekspert.co.uk" className="hover:text-blue-400 transition-colors">Contact Support</a></li>
                                <li><Link href="/#contact" className="hover:text-blue-400 transition-colors">Help Center</Link></li>
                                <li><Link href="/#features" className="hover:text-blue-400 transition-colors">Getting Started</Link></li>
                            </ul>
                        </div>

                        {/* Company */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Company</h4>
                            <ul className="space-y-2">
                                <li><Link href="/#about" className="hover:text-blue-400 transition-colors">About Us</Link></li>
                                <li><a href="https://tekspert.co.uk" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">TekSpert.co.uk</a></li>
                                <li><Link href="/privacy" className="text-blue-400">Privacy Policy</Link></li>
                                <li><Link href="/terms" className="hover:text-blue-400 transition-colors">Terms of Service</Link></li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-gray-400 text-sm">
                            © {new Date().getFullYear()} TekSpert Email. All rights reserved.
                        </p>
                        <div className="flex items-center space-x-6">
                            <a href="mailto:support@tekspert.co.uk" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">
                                support@tekspert.co.uk
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
